import { createSignal, For, Show, Suspense, Switch, Match } from "solid-js";
import { A, createAsync, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Button, Card, CardContent, Badge, Skeleton, EmptyState } from "~/components/ui";
import { GenerateReportModal } from "~/components/modals";
import { getReports, getReport } from "~/server/bff";
import { formatDate } from "~/lib/utils";
import { Plus, FileText, Calendar, Download, Copy, Tag, Check } from "lucide-solid";

export const route = {
  preload: () => getReports(),
} satisfies RouteDefinition;

export default function ReportsPage() {
  const reports = createAsync(() => getReports(), { deferStream: true });
  const [showGenerateReportModal, setShowGenerateReportModal] = createSignal(false);

  const openGenerateReportModal = () => setShowGenerateReportModal(true);

  const [copiedId, setCopiedId] = createSignal<string | null>(null);

  const handleDownload = async (e: Event, reportId: string, reportName: string) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Get full report content
      const report = await getReport(reportId);
      if (!report) return;

      // Create markdown content
      const content = `# ${report.name}\n\nGenerated: ${formatDate(report.createdAt)}\nType: ${report.type}\n${report.sourceName ? `Source: ${report.sourceName}\n` : ""}\n---\n\n${report.content || "No content available"}`;

      // Download as markdown file
      const blob = new Blob([content], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportName.replace(/[^a-z0-9]/gi, "_")}_report.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download report:", error);
    }
  };

  const handleCopy = async (e: Event, reportId: string) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Get full report content
      const report = await getReport(reportId);
      if (!report) return;

      // Copy content to clipboard
      const content = `# ${report.name}\n\n${report.content || "No content available"}`;
      await navigator.clipboard.writeText(content);

      // Show copied feedback
      setCopiedId(reportId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Failed to copy report:", error);
    }
  };

  return (
    <AppShell title="Reports">
      <div class="p-6">
        <PageHeader
          title="Reports & Briefings"
          description="Generated reports and briefing history"
          actions={
            <Button onClick={openGenerateReportModal}>
              <Plus class="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          }
        />

        <Suspense fallback={<ReportsSkeleton />}>
          <Show when={reports()} fallback={<ReportsSkeleton />}>
            {(items) => (
              <Show
                when={items().length > 0}
                fallback={
                  <EmptyState
                    icon={<FileText class="h-12 w-12" />}
                    title="No reports yet"
                    description="Generate reports from topics or dossiers, or enable daily briefings"
                    actionLabel="Generate Report"
                    onAction={openGenerateReportModal}
                  />
                }
              >
                <div class="space-y-3">
                  <For each={items()}>
                    {(report) => (
                      <A href={`/track/reports/${report.id}`}>
                        <Card class="group hover:border-primary/50 transition-colors">
                          <CardContent class="p-4">
                            <div class="flex items-start gap-4">
                              <div class="p-2 rounded-md bg-muted">
                                <TypeIcon type={report.type} />
                              </div>

                              <div class="flex-1 min-w-0">
                                <div class="flex items-start justify-between gap-2 mb-1">
                                  <h3 class="font-medium">{report.name}</h3>
                                  <TypeBadge type={report.type} />
                                </div>

                                <div class="flex items-center gap-3 text-sm text-muted-foreground">
                                  <span>{formatDate(report.createdAt)}</span>
                                  <Show when={report.sourceName}>
                                    <span>•</span>
                                    <span>Source: {report.sourceName}</span>
                                  </Show>
                                </div>
                              </div>

                              <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  title="Download Report"
                                  onClick={(e) => handleDownload(e, report.id, report.name)}
                                >
                                  <Download class="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  title={copiedId() === report.id ? "Copied!" : "Copy to clipboard"}
                                  onClick={(e) => handleCopy(e, report.id)}
                                >
                                  <Show when={copiedId() === report.id} fallback={<Copy class="h-4 w-4" />}>
                                    <Check class="h-4 w-4 text-green-500" />
                                  </Show>
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </A>
                    )}
                  </For>
                </div>
              </Show>
            )}
          </Show>
        </Suspense>
      </div>

      <Show when={showGenerateReportModal()}>
        <Suspense>
          <GenerateReportModal
            open={showGenerateReportModal()}
            onOpenChange={setShowGenerateReportModal}
          />
        </Suspense>
      </Show>
    </AppShell>
  );
}

function TypeIcon(props: { type: string }) {
  return (
    <Switch fallback={<FileText class="h-4 w-4" />}>
      <Match when={props.type === "daily_briefing" || props.type === "weekly_summary"}>
        <Calendar class="h-4 w-4" />
      </Match>
      <Match when={props.type === "topic_deep_dive"}>
        <Tag class="h-4 w-4" />
      </Match>
    </Switch>
  );
}

function TypeBadge(props: { type: string }) {
  return (
    <Switch fallback={<Badge variant="outline">{props.type}</Badge>}>
      <Match when={props.type === "daily_briefing"}>
        <Badge variant="secondary">Daily</Badge>
      </Match>
      <Match when={props.type === "weekly_summary"}>
        <Badge variant="secondary">Weekly</Badge>
      </Match>
      <Match when={props.type === "topic_deep_dive"}>
        <Badge variant="outline">
          <Tag class="h-3 w-3 mr-1" />
          Topic
        </Badge>
      </Match>
      <Match when={props.type === "channel_analysis"}>
        <Badge variant="outline">
          <FileText class="h-3 w-3 mr-1" />
          Channel
        </Badge>
      </Match>
      <Match when={props.type === "custom"}>
        <Badge variant="outline">Custom</Badge>
      </Match>
    </Switch>
  );
}

function ReportsSkeleton() {
  return (
    <div class="space-y-3">
      <For each={[1, 2, 3, 4]}>
        {() => (
          <Card>
            <CardContent class="p-4">
              <div class="flex items-start gap-4">
                <Skeleton class="h-10 w-10 rounded-md" />
                <div class="flex-1 space-y-2">
                  <Skeleton class="h-5 w-48" />
                  <Skeleton class="h-4 w-32" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}
